import { api } from "./baseApi";

export interface Tool {
	id: string;
	slug: string;
	name: string;
	description: string;
	isActive: boolean;
	isCustom?: boolean;
	department?: string;
}

export interface McpServer {
	id: string;
	name: string;
	url: string;
	apiKey: string;
	description: string;
	isActive: boolean;
	toolsJson: string;
	createdAt: string;
	updatedAt: string;
}

export interface McpToolInfo {
	name: string;
	description: string;
	inputSchema: Record<string, any>;
}

export interface ActivityLog {
	id: string;
	userId: string;
	userName: string;
	userEmail: string;
	department: string;
	method: string;
	path: string;
	toolSlug: string;
	notes: string;
	statusCode: number;
	createdAt: string;
}

export interface ActivityLogSummary {
	byTool: { toolSlug: string; count: number }[];
	byUser: { userId: string; userName: string; count: number }[];
	totalActions: number;
}

export interface ChatHistoryItem {
	id: string;
	title: string;
	createdAt: string;
	updatedAt: string;
	userId: string;
	user: { name: string; email: string; picture?: string };
}

export interface ChatHistoryDetail {
	id: string;
	title: string;
	messages: string; // JSON string
	createdAt: string;
	updatedAt: string;
	userId: string;
	user: { name: string; email: string };
}

export interface TriageTally {
	key: string;
	count: number;
}

export interface TriageStats {
	days: number;
	totals: {
		runs: number;
		analyses: number;
		messages: number;
		cases: number;
		users: number;
		jiraCreated: number;
		jiraDrafted: number;
		avgDurationMs: number;
		avgQuestions: number;
		flags: number;
		flagsOpen: number;
	};
	byVerdict: TriageTally[];
	byVerdictLatest: TriageTally[];
	byConfidence: TriageTally[];
	byProductArea: TriageTally[];
	byMode: TriageTally[];
	byUser: {
		userId: string;
		name: string;
		runs: number;
		analyses: number;
		cases: number;
		lastAt: string;
		lowConfidence: number;
		needsInfo: number;
	}[];
	daily: { date: string; analyses: number; messages: number; cases: number }[];
	topTools: TriageTally[];
}

export interface TriageFlagItem {
	id: string;
	userName: string;
	verdict: string;
	confidence: string;
	comment: string;
	createdAt: string;
	reviewedAt: string | null;
	reviewedBy: string;
}

export interface TriageReviewItem {
	id: string;
	caseNumber: string;
	userId: string;
	userName: string;
	conversationId: string;
	mode: string;
	verdict: string;
	confidence: string;
	productArea: string;
	summary: string;
	questionCount: number;
	openQuestions: number;
	jiraKey: string;
	jiraDrafted: boolean;
	runs: number;
	/** Duration of the latest run (ms) */
	durationMs: number;
	/** Every analysis run (analyze / re-analyze, not chat) on this case in the window, oldest → newest */
	durationsMs: number[];
	maxDurationMs: number;
	/** Model the latest run was asked to use; fellBack = primary failed and the run restarted on the fallback */
	model: string;
	fellBack: boolean;
	/** Tool display names the latest run used */
	tools: string[];
	/** Progress timeline of the latest run: ms since start per step */
	steps: { label: string; at: string; ms: number }[];
	at: string;
	flags: TriageFlagItem[];
	openFlags: number;
}

export interface AnalyticsData {
	totalActions: number;
	activeUsers: number;
	newChats: number;
	sharedChats: number;
	archivedChats: number;
	deletedChats: number;
	byTool: { name: string; count: number }[];
	mostActiveUsers: { userId: string; userName: string; count: number }[];
	topChatters: { userId: string; userName: string; count: number }[];
}

export interface ScheduledJob {
	id: string;
	name: string;
	prompt: string;
	enabled: boolean;
	summary: string;
	deliveryMethod: "email" | "slack" | "both" | "none";
	slackChannel: string;
	emailTo: string;
	timezone: string;
	nextRunAt: string | null;
	lastRunAt: string | null;
	lastStatus: string;
	lastError: string;
	createdAt: string;
	user: { name: string; email: string; picture?: string };
}

export interface UserWithAccess {
	id: string;
	googleId: string;
	email: string;
	name: string;
	picture: string;
	department: string;
	isAdmin: boolean;
	isDepartmentLead: boolean;
	departmentLeadOf: string;
	isActive: boolean;
	lastLoginAt: string | null;
	lastActivityAt: string | null;
	canManageTools: boolean;
	aiToolsJson: string;
	disabledMcpToolsJson: string;
	toolAccess: {
		id: string;
		tool: Tool;
	}[];
}

export const adminApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getUsers: builder.query<UserWithAccess[], void>({
			query: () => "/admin/users",
			providesTags: ["User"]
		}),
		// Every user (id/name/email) for the user filters on Chat History and User Logs
		getUserDirectory: builder.query<{ id: string; name: string; email: string }[], void>({
			query: () => "/admin/users/directory",
			providesTags: ["User"]
		}),
		getTools: builder.query<Tool[], void>({
			query: () => "/admin/tools"
		}),
		updateUserAdmin: builder.mutation<
			UserWithAccess,
			{ userId: string; isAdmin: boolean }
		>({
			query: ({ userId, isAdmin }) => ({
				url: `/admin/users/${userId}/admin`,
				method: "PUT",
				body: { isAdmin }
			}),
			invalidatesTags: ["User"]
		}),
		updateUserTools: builder.mutation<
			UserWithAccess,
			{ userId: string; toolSlugs: string[] }
		>({
			query: ({ userId, toolSlugs }) => ({
				url: `/admin/users/${userId}/tools`,
				method: "PUT",
				body: { toolSlugs }
			}),
			invalidatesTags: ["User"]
		}),
		syncUsers: builder.mutation<
			{ message: string; users: UserWithAccess[] },
			void
		>({
			query: () => ({
				url: "/admin/sync-users",
				method: "POST"
			}),
			invalidatesTags: ["User"]
		}),
		getSetting: builder.query<{ key: string; value: string }, string>({
			query: (key) => `/admin/settings/${key}`
		}),
		getSettingDefault: builder.query<{ key: string; value: string }, string>({
			query: (key) => `/admin/settings-defaults/${key}`
		}),
		updateSetting: builder.mutation<
			{ key: string; value: string },
			{ key: string; value: string }
		>({
			query: ({ key, value }) => ({
				url: `/admin/settings/${key}`,
				method: "PUT",
				body: { value }
			})
		}),
		getMcpServers: builder.query<McpServer[], void>({
			query: () => "/admin/mcp-servers",
			providesTags: ["McpServer"]
		}),
		createMcpServer: builder.mutation<
			McpServer,
			{ name: string; url: string; apiKey?: string; description?: string }
		>({
			query: (body) => ({
				url: "/admin/mcp-servers",
				method: "POST",
				body
			}),
			invalidatesTags: ["McpServer"]
		}),
		updateMcpServer: builder.mutation<
			McpServer,
			{ id: string; name?: string; url?: string; apiKey?: string; description?: string; isActive?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `/admin/mcp-servers/${id}`,
				method: "PUT",
				body
			}),
			invalidatesTags: ["McpServer"]
		}),
		deleteMcpServer: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({
				url: `/admin/mcp-servers/${id}`,
				method: "DELETE"
			}),
			invalidatesTags: ["McpServer"]
		}),
		refreshMcpTools: builder.mutation<{ tools: McpToolInfo[] }, string>({
			query: (id) => ({
				url: `/admin/mcp-servers/${id}/refresh-tools`,
				method: "POST"
			}),
			invalidatesTags: ["McpServer"]
		}),
		updateUserAiTools: builder.mutation<
			UserWithAccess,
			{ userId: string; aiTools: string[] }
		>({
			query: ({ userId, aiTools }) => ({
				url: `/admin/users/${userId}/ai-tools`,
				method: "PUT",
				body: { aiTools }
			}),
			invalidatesTags: ["User"]
		}),
		updateToolDepartment: builder.mutation<
			any,
			{ toolId: string; department: string }
		>({
			query: ({ toolId, department }) => ({
				url: `/admin/tools/${toolId}/department`,
				method: "PUT",
				body: { department }
			}),
			invalidatesTags: ["User"]
		}),
		updateUserDepartment: builder.mutation<
			UserWithAccess,
			{ userId: string; departments: string[] }
		>({
			query: ({ userId, departments }) => ({
				url: `/admin/users/${userId}/department`,
				method: "PUT",
				body: { departments }
			}),
			invalidatesTags: ["User"]
		}),
		updateUserDepartmentLead: builder.mutation<
			UserWithAccess,
			{ userId: string; departmentLeadOf: string }
		>({
			query: ({ userId, departmentLeadOf }) => ({
				url: `/admin/users/${userId}/department-lead`,
				method: "PUT",
				body: { departmentLeadOf }
			}),
			invalidatesTags: ["User"]
		}),
		updateUserToolBuilder: builder.mutation<
			UserWithAccess,
			{ userId: string; canManageTools: boolean }
		>({
			query: ({ userId, canManageTools }) => ({
				url: `/admin/users/${userId}/tool-builder`,
				method: "PUT",
				body: { canManageTools }
			}),
			invalidatesTags: ["User"]
		}),
		updateUserDisabledMcpTools: builder.mutation<
			UserWithAccess,
			{ userId: string; disabledMcpTools: string[] }
		>({
			query: ({ userId, disabledMcpTools }) => ({
				url: `/admin/users/${userId}/disabled-mcp-tools`,
				method: "PUT",
				body: { disabledMcpTools }
			}),
			invalidatesTags: ["User"]
		}),
		getActivityLogs: builder.query<
			{ logs: ActivityLog[]; total: number },
			{ page?: number; pageSize?: number; userId?: string; toolSlug?: string; startDate?: string; endDate?: string }
		>({
			query: (params) => ({ url: "/admin/activity-logs", params }),
			providesTags: ["ActivityLog"]
		}),
		getActivityLogSummary: builder.query<
			ActivityLogSummary,
			{ startDate?: string; endDate?: string }
		>({
			query: (params) => ({ url: "/admin/activity-logs/summary", params }),
			providesTags: ["ActivityLog"]
		}),
		getAnalytics: builder.query<AnalyticsData, { startDate?: string; endDate?: string }>({
			query: (params) => ({ url: "/admin/analytics", params }),
			providesTags: ["ActivityLog"]
		}),
		clearActivityLogs: builder.mutation<{ success: boolean; deleted: number }, void>({
			query: () => ({ url: "/admin/activity-logs", method: "DELETE" }),
			invalidatesTags: ["ActivityLog"]
		}),
		logPageView: builder.mutation<{ success: boolean }, { page: string; toolSlug: string; toolName?: string }>({
			query: (body) => ({ url: "/admin/page-view", method: "POST", body })
		}),
		getChatHistory: builder.query<
			{ chats: ChatHistoryItem[]; total: number },
			{ page?: number; pageSize?: number; userId?: string; search?: string; startDate?: string; endDate?: string }
		>({
			query: (params) => ({ url: "/admin/chat-history", params })
		}),
		getChatHistoryDetail: builder.query<ChatHistoryDetail, string>({
			query: (id) => `/admin/chat-history/${id}`
		}),
		getTriageStats: builder.query<TriageStats, number>({
			query: (days) => `/admin/triage/stats?days=${days}`
		}),
		setTriageFlagReviewed: builder.mutation<
			{ id: string; reviewedAt: string | null; reviewedBy: string },
			{ id: string; reviewed: boolean }
		>({
			query: ({ id, reviewed }) => ({
				url: `/admin/triage/flags/${id}`,
				method: "PATCH",
				body: { reviewed }
			})
		}),
		getTriageReview: builder.query<
			{ days: number; filter: string; total: number; items: TriageReviewItem[] },
			{ days: number; filter: string }
		>({
			query: ({ days, filter }) => `/admin/triage/review?days=${days}&filter=${encodeURIComponent(filter)}`
		}),
		deleteUser: builder.mutation<{ success: boolean; name: string }, string>({
			query: (userId) => ({ url: `/admin/users/${userId}`, method: "DELETE" }),
			invalidatesTags: ["User"]
		}),
		getScheduledJobs: builder.query<{ jobs: ScheduledJob[] }, void>({
			query: () => "/admin/scheduled-jobs",
			providesTags: ["ScheduledJob"]
		}),
		setScheduledJobEnabled: builder.mutation<
			{ id: string; enabled: boolean; nextRunAt: string | null },
			{ id: string; enabled: boolean }
		>({
			query: ({ id, enabled }) => ({
				url: `/admin/scheduled-jobs/${id}`,
				method: "PATCH",
				body: { enabled }
			}),
			invalidatesTags: ["ScheduledJob"]
		}),
		deleteScheduledJob: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({ url: `/admin/scheduled-jobs/${id}`, method: "DELETE" }),
			invalidatesTags: ["ScheduledJob"]
		})
	})
});

export const {
	useGetUsersQuery,
	useGetUserDirectoryQuery,
	useGetToolsQuery,
	useUpdateUserAdminMutation,
	useUpdateUserToolsMutation,
	useSyncUsersMutation,
	useLazyGetSettingQuery,
	useLazyGetSettingDefaultQuery,
	useUpdateSettingMutation,
	useGetMcpServersQuery,
	useCreateMcpServerMutation,
	useUpdateMcpServerMutation,
	useDeleteMcpServerMutation,
	useRefreshMcpToolsMutation,
	useUpdateUserAiToolsMutation,
	useUpdateUserDisabledMcpToolsMutation,
	useUpdateUserDepartmentMutation,
	useUpdateUserDepartmentLeadMutation,
	useUpdateUserToolBuilderMutation,
	useUpdateToolDepartmentMutation,
	useGetAnalyticsQuery,
	useClearActivityLogsMutation,
	useGetActivityLogsQuery,
	useGetActivityLogSummaryQuery,
	useLogPageViewMutation,
	useGetChatHistoryQuery,
	useLazyGetChatHistoryDetailQuery,
	useGetTriageStatsQuery,
	useGetTriageReviewQuery,
	useSetTriageFlagReviewedMutation,
	useDeleteUserMutation,
	useGetScheduledJobsQuery,
	useSetScheduledJobEnabledMutation,
	useDeleteScheduledJobMutation
} = adminApi;
